import React, { useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import { AllEnterpriseModule } from 'ag-grid-enterprise';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import withTranslate from '../../../containers/widgets/withTranslate';
import Icon, { IconSizes, IconVariants } from '../../../components/foundations/Icon';
import { isEmptyCellValue, parseCSVToGrid } from '../../../utils/csvUtils';
import { MAX_DISPLAY_ROWS } from '../../constants';
import GridToolbar from './GridToolbar';
import ArtifactLoader from './ArtifactLoader';
import css from './GridArtifact.css';

const DEFAULT_COL_DEF = {
    sortable: true,
    filter: true,
    resizable: true,
    minWidth: 50,
};

const MAX_GRID_HEIGHT = 450;

const API_DATE_FORMAT = 'YYYY-MM-DD';
const DATE_WITH_YEAR = 'MMM D, YYYY';
const DATE_NO_YEAR = 'MMM D';

const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return null;

    const start = moment(startDate, API_DATE_FORMAT, true);
    const end = moment(endDate, API_DATE_FORMAT, true);
    if (!start.isValid() || !end.isValid()) return null;

    if (start.isSame(end, 'day')) return end.format(DATE_WITH_YEAR);
    const startFormat = start.isSame(end, 'year') ? DATE_NO_YEAR : DATE_WITH_YEAR;
    return `${start.format(startFormat)} – ${end.format(DATE_WITH_YEAR)}`;
};

const GridArtifactInner = ({ data, metadata, contentUrl, title, translate }) => {
    const wrapperRef = useRef(null);
    const cardRef = useRef(null);
    const columnMetadata = metadata?.column_metadata;

    const { rowData, columnDefs, truncationInfo } = useMemo(() => {
        if (!data || typeof data !== 'string') {
            return { rowData: [], columnDefs: [], truncationInfo: null };
        }

        // Drop blank lines (notably the trailing newline) so the row count isn't
        // inflated by one — that off-by-one is what made a 50-row preview read as 51.
        const lines = data.split('\n').filter(line => line.trim());
        const totalRows = Math.max(0, lines.length - 1);
        let csvToRender = data;
        let truncInfo = null;

        // When the backend already truncated (streaming preview), `data` is only the
        // capped preview and `metadata` carries the authoritative full row_count — trust
        // it. Re-derive a client-side cap only when we hold the full dataset (reload path).
        if (!metadata?.is_truncated && totalRows > MAX_DISPLAY_ROWS) {
            csvToRender = lines.slice(0, MAX_DISPLAY_ROWS + 1).join('\n');
            truncInfo = {
                is_truncated: true,
                truncation_message: `Showing first ${MAX_DISPLAY_ROWS} of ${totalRows} rows for performance`,
                display_row_count: MAX_DISPLAY_ROWS,
                row_count: totalRows,
            };
        }

        const parseOptions = columnMetadata ? { columnMetadata } : undefined;
        const parsed = parseCSVToGrid(csvToRender, parseOptions);
        return {
            rowData: parsed.rowData,
            columnDefs: parsed.columnDefs.map(col => ({
                ...col,
                cellClassRules: { [css.emptyCell]: ({ value }) => isEmptyCellValue(value) },
            })),
            truncationInfo: truncInfo,
        };
    }, [data, columnMetadata, metadata?.is_truncated]);

    const displayMetadata = truncationInfo || metadata;

    if (!rowData || rowData.length === 0) {
        return (
            <div className={css.emptyState}>
                <Icon name="table" size={IconSizes.LARGE} />
                <div>No data available</div>
            </div>
        );
    }

    const handleFirstDataRendered = ({ api }) => {
        setTimeout(() => {
            api.autoSizeAllColumns();
            const wrapper = wrapperRef.current;
            const card = cardRef.current;
            if (!wrapper || !card) return;

            const totalWidth = api.getAllDisplayedColumns().reduce((sum, col) => sum + col.getActualWidth(), 0);

            if (totalWidth > 0) {
                const maxWidth = card.parentElement?.clientWidth ?? totalWidth;
                card.style.width = `${Math.min(totalWidth, maxWidth)}px`;
            }

            const headerEl = wrapper.querySelector('.ag-header');
            const headerH = headerEl ? headerEl.getBoundingClientRect().height : 49;
            const rowCount = api.getDisplayedRowCount();
            const naturalH = headerH + rowCount * 42;
            wrapper.style.height = `${Math.min(naturalH, MAX_GRID_HEIGHT)}px`;
        }, 0);
    };

    const dateRangeLabel = formatDateRange(metadata?.start_date, metadata?.end_date);

    return (
        <div className={css.gridArtifact}>
            <div ref={cardRef} className={css.gridCard}>
                <GridToolbar
                    data={data}
                    metadata={metadata}
                    contentUrl={contentUrl}
                    title={title}
                    dateRangeLabel={dateRangeLabel}
                    translate={translate}
                />
                <div ref={wrapperRef} className={`${css.gridWrapper} ag-theme-quartz`}>
                    <AgGridReact
                        modules={[AllEnterpriseModule]}
                        columnDefs={columnDefs}
                        rowData={rowData}
                        defaultColDef={DEFAULT_COL_DEF}
                        suppressColumnVirtualisation
                        onFirstDataRendered={handleFirstDataRendered}
                    />
                </div>
            </div>
            {displayMetadata?.is_truncated && (
                <div className={css.truncationWarning}>
                    <Icon name="info-circle" variant={IconVariants.WARNING} size={IconSizes.SMALL} />
                    <span>
                        {displayMetadata.truncation_message ||
                            `Showing ${displayMetadata.display_row_count} of ${displayMetadata.row_count} rows`}
                    </span>
                </div>
            )}
        </div>
    );
};

const GridArtifact = ({ artifact, translate }) => (
    <ArtifactLoader artifact={artifact}>
        {data => (
            <GridArtifactInner
                data={data}
                metadata={artifact.metadata}
                contentUrl={artifact.content_url}
                title={artifact.title || artifact.metadata?.title}
                translate={translate}
            />
        )}
    </ArtifactLoader>
);

GridArtifact.propTypes = {
    artifact: PropTypes.shape({
        id: PropTypes.string.isRequired,
        type: PropTypes.string.isRequired,
        data: PropTypes.string,
        title: PropTypes.string,
        content_url: PropTypes.string,
        metadata: PropTypes.shape({
            title: PropTypes.string,
            display_row_count: PropTypes.number,
            is_truncated: PropTypes.bool,
            row_count: PropTypes.number,
            truncation_message: PropTypes.string,
            columns: PropTypes.arrayOf(PropTypes.string),
            start_date: PropTypes.string,
            end_date: PropTypes.string,
        }),
    }).isRequired,
    translate: PropTypes.func.isRequired,
};

export default withTranslate(GridArtifact);
